import { Component, OnInit, ViewEncapsulation, HostBinding, NgZone, HostListener } from '@angular/core';
import { environment } from 'environments/environment';
import { AppStateService, IStateQueryParams } from './core/app-state.service';
import { ISegment, IDemandsIndexChangeEvent } from './core/nav-bar/nav-bar.component';
import { LoadingStateService } from 'viega-common/services/loading-state-service';
import { IMobileMenuStateEvent } from 'viega-common/components/viega-header/viega-header.component';
import { Router, ActivationEnd } from '@angular/router';
import { LangDataService } from './core/lang-data.service';
import { WebTrekkService } from './core/web-trekk.service';

// CMP variables
declare const UC_UI: any;

/**
 * App Component
 * Top Level Component
 */
@Component({
    selector: 'app',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
    /** Languages / countries which should be redirected to the bpk1 */
    private static readonly Bpk1Langs = [];
    /** Relative url to the old version 1 configurator. */
    private static readonly BPK1RedirectUrl = '/v1/';

    public navBarSegments: ISegment[];
    public showDevModule: boolean = environment.showDevModule;

    @HostBinding('class.trade-fair')
    public get isTradeFairMode(): boolean {
        return this.appState.isTradeFairMode;
    }

    public get areHeaderLinksAvailable(): boolean {
        return !this.appState.isTradeFairMode;
    }

    public get titleHref(): string {
        if (this.appState.isTradeFairMode)
            return undefined;
        else
            return this.lang.getString('header-viega-name@href');
    }

    public get enabledCookieHint(): boolean {
        const notInTradeFairMode = !this.appState.isTradeFairMode;
        const translationExists = this.lang.getString('header-viega-cookie-text', '') !== '';

        return notInTradeFairMode && translationExists;
    }

    /**
     * Returns false, if the detected language is currently not support by the BPK2 and a
     * redirect to the BPK1 should be performed.
     */
    public get languageIsSupported(): boolean {
        return AppComponent.Bpk1Langs.indexOf(this.appState.languageId) === -1;
    }

    constructor(
        private appState: AppStateService,
        private router: Router,
        private zone: NgZone,
        private loadingService: LoadingStateService,
        public lang: LangDataService,
        private tracker: WebTrekkService,
    ) {}

    public async ngOnInit(): Promise<void> {
        this.loadingService.addLoad();

        // wait for the first route navigation to complete
        // it has to complete, so we have an actual route snapshot available
        // then load the correct json data source by requesting the labels
        const subscription = this.router.events.subscribe(event => {
            if (event instanceof ActivationEnd) {
                if (this.languageIsSupported)
                    this.lang.loadData(() => this.languageDataAvailable());
                else
                    this.redirectToBpk1();

                subscription.unsubscribe();
            }
        }, (error) => {
            this.loadingService.removeLoad();
            console.error(error);
        });

        // Add consent change event listener
        window.addEventListener("UC_SDK_EVENT", (args: any) => {    
            if(args.detail.event === 'consent_status' && args.detail.action !== 'onInitialPageLoad') {
                window.location.reload();
            }
        });
    }

    private languageDataAvailable() {
        this.tracker.initTracker();

        this.navBarSegments = [
            { label: this.lang.getString("segment-appliance-head") },
            { label: this.lang.getString("segment-design-head") },
            { label: this.lang.getString("segment-color-head") },
            { label: this.lang.getString("segment-milieu-head") },
            { label: this.lang.getString("segment-prewall-head") },
            { label: this.lang.getString("segment-result-head") },
        ];
        this.applyPageTitle(this.lang.getString("bpconf-title-name"));
        this.applyLanguageAttribute(this.appState.languageId);

        this.loadingService.removeLoad();
    }

    public header_mobileMenuStateChanged(event: IMobileMenuStateEvent) {
        const menuState = event.newMenuState;
        console.assert(typeof menuState === "boolean", "Unexpected menuState value.");

        this.appState.isMobileMenuOpen = menuState;
    }

    public navBar_demandsIndexChange(event: IDemandsIndexChangeEvent) {
        if (this.appState.activeStep !== event.newStepIndex) {
            this.appState.activeStep = event.newStepIndex;

            const route = this.appState.stepIndexToRoute(event.newStepIndex);
            this.appState.changeRoute(route, this.appState.urlQueryParamsMap());
        }
    }

    /** Applies a "lang" attribute to the html root element, so that the browser is locale aware when performing hyphenation. */
    private applyLanguageAttribute(languageId: string) {
        const htmlElement = document.getElementsByTagName("html")[0];
        htmlElement.setAttribute("lang", languageId);
    }

    private applyPageTitle(newTitle: string) {
        const htmlElement = document.getElementsByTagName("title")[0];
        htmlElement.innerText = newTitle;
    }

    public getLogoQueryParams(): IStateQueryParams {
        return this.appState.urlQueryParamsBaseMap();
    }

    public openBackUrl() {
        window.location.href = this.appState.backUrl;
    }

    public redirectToBpk1() {
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        const pathWithoutFileName = pathname.replace('/index.html', '');

        let destinationPath = AppComponent.BPK1RedirectUrl;
        destinationPath = pathWithoutFileName + destinationPath;

        let destinationUrl = hostname + '/' + destinationPath;
        // remove redundant slashes
        destinationUrl = destinationUrl.replace(/\/+/g, '/');
        destinationUrl = protocol + '//' + destinationUrl;

        console.log('redirecting to: ' + destinationUrl);
        window.location.replace(destinationUrl);
    }

    public showCookieConfigurationModal(): void {
        UC_UI.showSecondLayer();
    }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts